import type { AnimatedIconComponent } from '../../components/icons';
import {
  BriefcaseBusinessIcon,
  CalendarDaysIcon,
  ChartSplineIcon,
  CircleCheckIcon,
  ClipboardCheckIcon,
  FileStackIcon,
  FileTextIcon,
  FolderOpenIcon,
  HandCoinsIcon,
  LayoutGridIcon,
  SparklesIcon,
  UserIcon,
  UsersIcon,
  VideoIcon,
  ZapIcon,
} from '../../components/icons';

/** @deprecated Use NavEntry for sidebar config */
export type NavItem = {
  label: string;
  path: string;
  icon: AnimatedIconComponent;
  badge?: string;
};

export type NavChild = {
  label: string;
  path: string;
  icon: AnimatedIconComponent;
  badge?: string;
};

export type NavLinkEntry = {
  type: 'link';
  label: string;
  path: string;
  icon: AnimatedIconComponent;
  badge?: string;
};

export type NavGroupEntry = {
  type: 'group';
  label: string;
  icon: AnimatedIconComponent;
  items: NavChild[];
};

export type NavDividerEntry = {
  type: 'divider';
};

export type NavEntry = NavLinkEntry | NavGroupEntry | NavDividerEntry;

/** Service nav items keyed by feature ID */
const JOB_SEEKER_SERVICE_MAP: Record<string, NavChild> = {
  'resume-review': { label: 'Resume Review', path: '/jobseeker-dashboard/resume-review', icon: FileTextIcon },
  'culture-call': { label: 'Mock Interview', path: '/jobseeker-dashboard/mock-interview', icon: VideoIcon },
  'referral-session': { label: 'Referral Session', path: '/jobseeker-dashboard/referral-session', icon: UsersIcon },
};

const REFERRER_JOBSEEKER_SERVICE_MAP: Record<string, NavChild> = {
  'resume-review': {
    label: 'Resume Review',
    path: '/referral-dashboard/jobseeker-dashboard/resume-review',
    icon: FileTextIcon,
  },
  'culture-call': {
    label: 'Mock Interview',
    path: '/referral-dashboard/jobseeker-dashboard/mock-interview',
    icon: VideoIcon,
  },
  'referral-session': {
    label: 'Referral Session',
    path: '/referral-dashboard/jobseeker-dashboard/referral-session',
    icon: UsersIcon,
  },
};

/** Build cohesive, categorized job-seeker nav */
export function buildJobSeekerNav(enabledFeatures: string[] = []): NavEntry[] {
  const serviceItems = Object.entries(JOB_SEEKER_SERVICE_MAP)
    .filter(([key]) => enabledFeatures.length === 0 || enabledFeatures.includes(key))
    .map(([, child]) => child);

  return [
    { type: 'link', label: 'Dashboard', path: '/jobseeker-dashboard', icon: LayoutGridIcon },
    { type: 'divider' },
    {
      type: 'group',
      label: 'Referrals & Drops',
      icon: HandCoinsIcon,
      items: [
        { label: 'Weekly Drops', path: '/jobseeker-dashboard/referral-drops', icon: HandCoinsIcon },
        { label: 'My Applications', path: '/jobseeker-dashboard/my-applications', icon: FileStackIcon },
        { label: 'Job Openings', path: '/jobseeker-dashboard/jobopenings', icon: BriefcaseBusinessIcon },
        { label: 'Job Applications', path: '/jobseeker-dashboard/my-job-applications', icon: FileTextIcon },
      ],
    },
    {
      type: 'group',
      label: '1:1 Mentorship',
      icon: UsersIcon,
      items: [
        ...serviceItems,
        { label: 'My Sessions', path: '/jobseeker-dashboard/my-sessions', icon: CalendarDaysIcon },
      ],
    },
    {
      type: 'group',
      label: 'Proof & Trust',
      icon: CircleCheckIcon,
      items: [
        { label: 'Verified Referral Card', path: '/jobseeker-dashboard/career-proof', icon: ClipboardCheckIcon },
        { label: 'Role Passports', path: '/jobseeker-dashboard/passports', icon: FileTextIcon },
      ],
    },
    {
      type: 'group',
      label: 'Tools & Account',
      icon: FolderOpenIcon,
      items: [
        { label: 'How Hireable Are You?', path: '/jobseeker-dashboard/hireability', icon: ZapIcon, badge: 'New' },
        { label: 'AI Resume Grader', path: '/jobseeker-dashboard/resume-grader', icon: SparklesIcon },
        { label: 'Invoices', path: '/jobseeker-dashboard/invoices', icon: FileTextIcon },
        { label: 'My Profile', path: '/jobseeker-dashboard/profile', icon: UserIcon },
      ],
    },
  ];
}

/** Build cohesive employee-as-jobseeker nav */
export function buildEmployeeAsJobseekerNav(enabledFeatures: string[] = []): NavEntry[] {
  const serviceItems = Object.entries(REFERRER_JOBSEEKER_SERVICE_MAP)
    .filter(([key]) => enabledFeatures.length === 0 || enabledFeatures.includes(key))
    .map(([, child]) => child);

  return [
    { type: 'link', label: 'Dashboard', path: '/referral-dashboard/jobseeker-dashboard', icon: LayoutGridIcon },
    { type: 'divider' },
    {
      type: 'group',
      label: 'Referrals & Drops',
      icon: HandCoinsIcon,
      items: [
        { label: 'Weekly Drops', path: '/referral-dashboard/jobseeker-dashboard/referral-drops', icon: HandCoinsIcon },
        { label: 'My Applications', path: '/referral-dashboard/jobseeker-dashboard/my-applications', icon: FileStackIcon },
        { label: 'Job Openings', path: '/referral-dashboard/jobseeker-dashboard/jobopenings', icon: BriefcaseBusinessIcon },
        { label: 'Job Applications', path: '/referral-dashboard/jobseeker-dashboard/my-job-applications', icon: FileTextIcon },
      ],
    },
    {
      type: 'group',
      label: '1:1 Mentorship',
      icon: UsersIcon,
      items: [
        ...serviceItems,
        { label: 'My Sessions', path: '/referral-dashboard/jobseeker-dashboard/my-sessions', icon: CalendarDaysIcon },
      ],
    },
    {
      type: 'group',
      label: 'Proof & Trust',
      icon: CircleCheckIcon,
      items: [
        { label: 'Verified Referral Card', path: '/referral-dashboard/jobseeker-dashboard/career-proof', icon: ClipboardCheckIcon },
        { label: 'Role Passports', path: '/referral-dashboard/jobseeker-dashboard/passports', icon: FileTextIcon },
      ],
    },
    {
      type: 'group',
      label: 'Tools & Account',
      icon: FolderOpenIcon,
      items: [
        {
          label: 'How Hireable Are You?',
          path: '/referral-dashboard/jobseeker-dashboard/hireability',
          icon: ZapIcon,
          badge: 'New',
        },
        { label: 'AI Resume Grader', path: '/referral-dashboard/jobseeker-dashboard/resume-grader', icon: SparklesIcon },
        { label: 'Invoices', path: '/referral-dashboard/jobseeker-dashboard/invoices', icon: FileTextIcon },
        { label: 'My Profile', path: '/referral-dashboard/jobseeker-dashboard/profile', icon: UserIcon },
      ],
    },
  ];
}

/** Career Trust candidate nav helper */
export function buildCandidateTrustNav(
  _flags: Record<string, boolean>,
  _base: '/jobseeker-dashboard' | '/referral-dashboard/jobseeker-dashboard'
): NavEntry[] {
  // Integrated directly into the main nav groups above
  return [];
}

/** Career Trust professional nav (verification inbox) */
export function buildProfessionalTrustNav(_flags?: Record<string, boolean>): NavEntry[] {
  return [];
}

/** Cohesive Professional/Referrer Navigation */
export function buildReferrerNav(): NavEntry[] {
  return [
    { type: 'link', label: 'Dashboard', path: '/referral-dashboard', icon: LayoutGridIcon },
    { type: 'divider' },
    {
      type: 'group',
      label: 'Referral Hub',
      icon: HandCoinsIcon,
      items: [
        { label: 'Weekly Candidate Drops', path: '/referral-dashboard/referral-drops', icon: HandCoinsIcon },
        { label: 'Verification Requests', path: '/referral-dashboard/verification-requests', icon: CircleCheckIcon },
        { label: 'Post a Job Opening', path: '/referral-dashboard/post-job', icon: BriefcaseBusinessIcon },
      ],
    },
    {
      type: 'group',
      label: 'Services & Bookings',
      icon: FolderOpenIcon,
      items: [
        { label: 'My Offerings', path: '/referral-dashboard/services', icon: BriefcaseBusinessIcon },
        { label: 'Client Sessions', path: '/referral-dashboard/my-sessions', icon: CalendarDaysIcon },
        { label: 'Availability Calendar', path: '/referral-dashboard/calendar', icon: CalendarDaysIcon },
      ],
    },
    {
      type: 'group',
      label: 'Earnings & Analytics',
      icon: ChartSplineIcon,
      items: [
        { label: 'Performance Analytics', path: '/referral-dashboard/analytics', icon: ChartSplineIcon },
        { label: 'Payout Transfers', path: '/referral-dashboard/payouts', icon: HandCoinsIcon },
        { label: 'Invoices', path: '/referral-dashboard/invoices', icon: FileTextIcon },
      ],
    },
    { type: 'link', label: 'My Profile', path: '/referral-dashboard/profile', icon: UserIcon },
  ];
}

/** Static fallbacks */
export const JOB_SEEKER_NAV: NavEntry[] = buildJobSeekerNav(['resume-review', 'culture-call', 'referral-session']);
export const EMPLOYEE_AS_JOBSEEKER_NAV: NavEntry[] = buildEmployeeAsJobseekerNav(['resume-review', 'culture-call', 'referral-session']);
export const EMPLOYEE_NAV: NavEntry[] = buildReferrerNav();

/** Flat list kept for legacy references */
export const JOB_SEEKER_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/jobseeker-dashboard', icon: LayoutGridIcon },
  { label: 'Weekly Drops', path: '/jobseeker-dashboard/referral-drops', icon: HandCoinsIcon },
  { label: 'My Applications', path: '/jobseeker-dashboard/my-applications', icon: FileStackIcon },
  { label: 'Hireability', path: '/jobseeker-dashboard/hireability', icon: ZapIcon },
  { label: 'AI Grader', path: '/jobseeker-dashboard/resume-grader', icon: SparklesIcon },
  { label: 'Resume Review', path: '/jobseeker-dashboard/resume-review', icon: FileTextIcon },
  { label: 'Mock Interview', path: '/jobseeker-dashboard/mock-interview', icon: VideoIcon },
  { label: 'Referral Session', path: '/jobseeker-dashboard/referral-session', icon: UsersIcon },
  { label: 'Job Openings', path: '/jobseeker-dashboard/jobopenings', icon: BriefcaseBusinessIcon },
  { label: 'My Sessions', path: '/jobseeker-dashboard/my-sessions', icon: CalendarDaysIcon },
  { label: 'Career Proof', path: '/jobseeker-dashboard/career-proof', icon: ClipboardCheckIcon },
  { label: 'Passports', path: '/jobseeker-dashboard/passports', icon: FileTextIcon },
  { label: 'Invoices', path: '/jobseeker-dashboard/invoices', icon: FileTextIcon },
  { label: 'My Profile', path: '/jobseeker-dashboard/profile', icon: UserIcon },
];

export const EMPLOYEE_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/referral-dashboard', icon: LayoutGridIcon },
  { label: 'Referral Drops', path: '/referral-dashboard/referral-drops', icon: HandCoinsIcon },
  { label: 'Verification', path: '/referral-dashboard/verification-requests', icon: CircleCheckIcon },
  { label: 'My Services', path: '/referral-dashboard/services', icon: BriefcaseBusinessIcon },
  { label: 'Post a Job', path: '/referral-dashboard/post-job', icon: BriefcaseBusinessIcon },
  { label: 'My Postings', path: '/referral-dashboard/my-postings', icon: FileStackIcon },
  { label: 'My Sessions', path: '/referral-dashboard/my-sessions', icon: CalendarDaysIcon },
  { label: 'Calendar', path: '/referral-dashboard/calendar', icon: CalendarDaysIcon },
  { label: 'Payouts', path: '/referral-dashboard/payouts', icon: ChartSplineIcon },
  { label: 'Analytics', path: '/referral-dashboard/analytics', icon: ChartSplineIcon },
  { label: 'Invoices', path: '/referral-dashboard/invoices', icon: FileTextIcon },
  { label: 'My Profile', path: '/referral-dashboard/profile', icon: UserIcon },
];

export const EMPLOYEE_AS_JOBSEEKER_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/referral-dashboard/jobseeker-dashboard', icon: LayoutGridIcon },
  { label: 'Weekly Drops', path: '/referral-dashboard/jobseeker-dashboard/referral-drops', icon: HandCoinsIcon },
  { label: 'My Applications', path: '/referral-dashboard/jobseeker-dashboard/my-applications', icon: FileStackIcon },
  { label: 'Hireability', path: '/referral-dashboard/jobseeker-dashboard/hireability', icon: ZapIcon },
  { label: 'AI Grader', path: '/referral-dashboard/jobseeker-dashboard/resume-grader', icon: SparklesIcon },
  { label: 'Resume Review', path: '/referral-dashboard/jobseeker-dashboard/resume-review', icon: FileTextIcon },
  { label: 'Mock Interview', path: '/referral-dashboard/jobseeker-dashboard/mock-interview', icon: VideoIcon },
  { label: 'Referral Session', path: '/referral-dashboard/jobseeker-dashboard/referral-session', icon: UsersIcon },
  { label: 'Job Openings', path: '/referral-dashboard/jobseeker-dashboard/jobopenings', icon: BriefcaseBusinessIcon },
  { label: 'My Sessions', path: '/referral-dashboard/jobseeker-dashboard/my-sessions', icon: CalendarDaysIcon },
  { label: 'Career Proof', path: '/referral-dashboard/jobseeker-dashboard/career-proof', icon: ClipboardCheckIcon },
  { label: 'Passports', path: '/referral-dashboard/jobseeker-dashboard/passports', icon: FileTextIcon },
  { label: 'Invoices', path: '/referral-dashboard/jobseeker-dashboard/invoices', icon: FileTextIcon },
  { label: 'My Profile', path: '/referral-dashboard/jobseeker-dashboard/profile', icon: UserIcon },
];

export function flatNavToEntries(items: NavItem[]): NavEntry[] {
  return items.map((item) => ({ type: 'link', ...item }));
}

export function findNavLabel(entries: NavEntry[], _pathname: string, isActive: (path: string) => boolean): string {
  for (const entry of entries) {
    if (entry.type === 'link' && isActive(entry.path)) {
      return entry.label;
    }
    if (entry.type === 'group') {
      const child = entry.items.find((item) => isActive(item.path));
      if (child) return child.label;
    }
  }
  return 'Dashboard';
}
